import React, { useCallback, useEffect, useState } from "react";
import AppShell from "../layouts/AppShell";
import { appModules } from "../components/global";
import { GET, PUT, DELETE, loading, alert, toFixed, format_date } from "../components/CustomComponent";
import TaxCalendar from "../components/company-tax/TaxCalendar";
import ForecastPanel from "../components/company-tax/ForecastPanel";
import CorporateTaxPanel from "../components/company-tax/CorporateTaxPanel";
import FilingModal from "../components/company-tax/FilingModal";
import { cardStyle, toThaiYear } from "../components/company-tax/constants";

// ภาษีของบริษัท: ภ.ง.ด.1 / 3 / 53, ภ.พ.30, ประกันสังคม และภาษีนิติบุคคล ภ.ง.ด.50/51
// ตัวเลขคำนวณจากสลิปเงินเดือนกับธุรกรรมการเงินในระบบ แล้วกรอกยอดที่ยื่นจริงทับได้
const API = "v1/admin/company-taxes";

const TABS = [
  { value: "calendar", label: "ปฏิทินภาษีรายเดือน", icon: "fa-calendar-check" },
  { value: "forecast", label: "พยากรณ์ล่วงหน้า", icon: "fa-chart-line" },
  { value: "corporate", label: "ภาษีนิติบุคคล", icon: "fa-building-columns" },
];

const StatCard = ({ label, value, color, hint }) => (
  <div className="col-6 col-lg-3">
    <div className="rounded-4 p-3 p-md-4 h-100" style={cardStyle}>
      <div className="small text-secondary mb-1">{label}</div>
      <div className="fw-bold" style={{ color, fontSize: "clamp(1rem, 2.4vw, 1.5rem)" }}>{value}</div>
      {hint ? <div className="text-secondary" style={{ fontSize: "0.75rem" }}>{hint}</div> : null}
    </div>
  </div>
);

export default function Tax() {
  const { title, description } = appModules["tax"];
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [years, setYears] = useState([currentYear]);
  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [horizon, setHorizon] = useState(12);
  const [tab, setTab] = useState("calendar");
  const [activeCell, setActiveCell] = useState(null);

  const loadYears = useCallback(async () => {
    const result = await GET(`${API}/years`);
    if (result?.status && result.data?.length) setYears(result.data);
  }, []);

  const loadSummary = useCallback(async () => {
    loading(true);
    const result = await GET(`${API}/summary`, { year });
    if (result?.status) setSummary(result.data);
    loading(false);
  }, [year]);

  const loadForecast = useCallback(async () => {
    const result = await GET(`${API}/forecast`, { months: horizon });
    if (result?.status) setForecast(result.data);
  }, [horizon]);

  useEffect(() => {
    loadYears();
  }, [loadYears]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadForecast();
  }, [loadForecast]);

  const handleSaveFiling = async (cell, form) => {
    const body = {
      tax_year: year,
      period_month: cell.period_month || undefined,
      form_type: cell.form_type,
      base_amount: Number(form.base_amount) || 0,
      tax_amount: Number(form.tax_amount) || 0,
      surcharge: Number(form.surcharge) || 0,
      penalty: Number(form.penalty) || 0,
      status: form.status,
      filed_date: form.filed_date || null,
      reference_no: form.reference_no || null,
      note: form.note || null,
    };

    loading(true);
    const result = await PUT(`${API}/filings`, body);
    loading(false);

    if (!result?.status) {
      alert("", "warning", "บันทึกไม่สำเร็จ", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
      return;
    }
    setActiveCell(null);
    loadSummary();
  };

  // ล้างค่าที่กรอกทับ กลับไปใช้ตัวเลขที่ระบบคำนวณจากข้อมูลจริง
  const handleResetFiling = async (cell) => {
    if (!cell?.uid) return;
    if (!(await alert("", "warning", "ล้างค่าที่กรอก", "กลับไปใช้ตัวเลขที่ระบบคำนวณให้ใช่หรือไม่", "ล้างค่า", "ยกเลิก"))) return;

    loading(true);
    const result = await DELETE(`${API}/filings/${cell.uid}`, null);
    loading(false);

    if (result?.status) {
      setActiveCell(null);
      loadSummary();
    }
  };

  const totals = summary?.totals || {};
  const alerts = summary?.alerts || [];
  const overdueAlerts = alerts.filter((item) => item.level === "overdue");
  const upcomingAlerts = alerts.filter((item) => item.level === "upcoming");

  return (
    <AppShell title={title} description={description}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div className="d-flex gap-2 flex-wrap">
          {TABS.map((item) => (
            <button
              key={item.value}
              className={`btn ${tab === item.value ? "btn-primary" : "btn-outline-primary"} rounded-3`}
              onClick={() => setTab(item.value)}
            >
              <i className={`fa-solid ${item.icon} me-2`} />{item.label}
            </button>
          ))}
        </div>

        <select className="form-select" style={{ width: 180, borderRadius: 12 }} value={year} onChange={(event) => setYear(Number(event.target.value))}>
          {years.map((option) => (
            <option key={option} value={option}>ปีภาษี {toThaiYear(option)}</option>
          ))}
        </select>
      </div>

      {overdueAlerts.length > 0 && (
        <div className="rounded-4 p-3 p-md-4 mb-4" style={{ background: "rgba(229, 83, 83, 0.08)", border: "1px solid rgba(229, 83, 83, 0.28)" }}>
          <div className="fw-semibold mb-2" style={{ color: "#b63636" }}>
            <i className="fa-solid fa-triangle-exclamation me-2" />ค้างยื่น {overdueAlerts.length} รายการ
          </div>
          <div className="d-flex flex-wrap gap-2">
            {overdueAlerts.map((item) => (
              <span key={`${item.form_type}-${item.period_month}`} className="rounded-pill px-3 py-1 small bg-white" style={{ color: "#b63636" }}>
                {item.label} งวด {item.period_month} · {toFixed(item.amount)} บาท
                {item.suggested_surcharge > 0 ? ` (+เงินเพิ่ม ${toFixed(item.suggested_surcharge)})` : ""}
              </span>
            ))}
          </div>
        </div>
      )}

      {upcomingAlerts.length > 0 && (
        <div className="rounded-4 p-3 p-md-4 mb-4" style={{ background: "rgba(245, 166, 35, 0.10)", border: "1px solid rgba(245, 166, 35, 0.30)" }}>
          <div className="fw-semibold mb-2" style={{ color: "#b97007" }}>
            <i className="fa-solid fa-clock me-2" />ใกล้ครบกำหนดยื่น
          </div>
          <div className="d-flex flex-wrap gap-2">
            {upcomingAlerts.map((item) => (
              <span key={`${item.form_type}-${item.period_month}`} className="rounded-pill px-3 py-1 small bg-white" style={{ color: "#b97007" }}>
                {item.label} งวด {item.period_month} · เหลืออีก {item.days_left} วัน (ถึง {format_date(item.efiling_due_date, "dd/mm/yyyy")}) · {toFixed(item.amount)} บาท
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="row g-3 mb-4">
        <StatCard label={`ภาษีทั้งปี ${toThaiYear(year)}`} value={toFixed(totals.tax_total)} color="#0c76e5" hint="รวมทุกแบบที่ต้องนำส่ง" />
        <StatCard label="ยื่น/จ่ายแล้ว" value={toFixed(totals.paid_total)} color="#188251" />
        <StatCard label="ค้างยื่น" value={toFixed(totals.pending_total)} color="#b97007" hint="รวมงวดที่ยังไม่ถึงกำหนด" />
        <StatCard label="เกินกำหนดแล้ว" value={toFixed(totals.overdue_total)} color="#b63636" hint={`${overdueAlerts.length} รายการ`} />
      </div>

      {tab === "calendar" && <TaxCalendar summary={summary} year={year} onOpenCell={setActiveCell} />}

      {tab === "forecast" && <ForecastPanel forecast={forecast} horizon={horizon} setHorizon={setHorizon} />}

      {tab === "corporate" && <CorporateTaxPanel annual={summary?.annual} year={year} onOpenCell={setActiveCell} />}

      <div className="small text-secondary mt-3">
        ตัวเลขคำนวณจากสลิปเงินเดือนที่จ่ายแล้วและธุรกรรมการเงินที่บันทึกไว้ในระบบ ใช้วางแผนและตรวจทานก่อนยื่นจริง
        ก่อนนำส่งกรมสรรพากรให้ตรวจกับเอกสารตัวจริงอีกครั้ง
      </div>

      <FilingModal
        show={!!activeCell}
        cell={activeCell}
        year={year}
        onHide={() => setActiveCell(null)}
        onSave={handleSaveFiling}
        onReset={handleResetFiling}
      />
    </AppShell>
  );
}
